import { message } from 'antd';

interface ApiErrorData {
  message?: string;
  errors?: Record<string, string[]>;
}

interface ApiError {
  status?: number;
  data?: ApiErrorData;
}

/**
 * Normalizes error input — handles direct error `{ status, data }` (from `.unwrap()`),
 * RTK response wrapper `{ error: { status, data } }` (from `if ('error' in response)`),
 * and axios errors `{ response: { status, data } }` (e.g. Laravel Precognition `form.submit().catch()`).
 */
const normalizeError = (error: unknown): ApiError | undefined => {
  const obj = error as Record<string, unknown> | undefined;
  if (!obj || typeof obj !== 'object') return undefined;

  // RTK response shape: { error: { status, data } }
  if ('error' in obj && typeof obj.error === 'object' && obj.error !== null) {
    return obj.error as ApiError;
  }

  // Axios error shape (e.g. Laravel Precognition): { response: { status, data } }
  if ('response' in obj && typeof obj.response === 'object' && obj.response !== null) {
    return obj.response as ApiError;
  }

  // Direct error shape: { status, data }
  return obj as unknown as ApiError;
};

/**
 * The backend refuses every launch path (desk Launch, editor Launch, resume,
 * status toggles, approving a creative into a live campaign) for an
 * organization without an active Boost PRO with a 422 on this key.
 */
const PRO_REQUIRED_ERROR_KEY = 'subscription';

let proRequiredHandler: (() => void) | null = null;

/**
 * Registers the app-wide Boost PRO paywall (mounted once by `BillingProvider`).
 * Returns the unregister callback for the effect cleanup.
 */
export const setProRequiredHandler = (handler: () => void): (() => void) => {
  proRequiredHandler = handler;

  return () => {
    if (proRequiredHandler === handler) {
      proRequiredHandler = null;
    }
  };
};

/** True when the error is the launch gate's "Boost PRO is required" refusal. */
export const isProRequiredError = (error: unknown): boolean => {
  const err = normalizeError(error);

  return err?.status === 422 && Boolean(err?.data?.errors?.[PRO_REQUIRED_ERROR_KEY]?.length);
};

/**
 * Opens the Boost PRO paywall when the error is the launch gate's refusal.
 * Returns true when it did — the caller then shows nothing else for it.
 */
export const handleProRequiredError = (error: unknown): boolean => {
  if (!proRequiredHandler || !isProRequiredError(error)) return false;

  proRequiredHandler();

  return true;
};

/**
 * Extracts field-level validation errors from a Laravel 422 response.
 * Returns Record<fieldName, firstErrorMessage> — suitable for Form.Item display.
 *
 * Accepts both shapes:
 * - Direct error: `extractFieldErrors(response.error)`
 * - RTK response: `extractFieldErrors(response)`
 */
export const extractFieldErrors = (error: unknown): Record<string, string> => {
  const err = normalizeError(error);
  if (err?.data?.errors) {
    const result: Record<string, string> = {};
    Object.entries(err.data.errors).forEach(([key, messages]) => {
      if (messages?.[0]) {
        result[key] = messages[0];
      }
    });
    return result;
  }
  return {};
};

/**
 * Standardized API error handler for the entire application.
 *
 * Accepts both shapes:
 * - Direct error (from `.unwrap()` catch): `showApiError(err, 'Failed')`
 * - RTK response (from `if ('error' in response)`): `showApiError(response, 'Failed')`
 *
 * **Two modes:**
 * 1. **Form context** (`showAllErrors: false`, default) — shows one toast,
 *    returns field errors for `Form.Item` display.
 * 2. **Non-form context** (`showAllErrors: true`) — shows every validation error
 *    as a separate `message.error` toast.
 *
 * @example
 * // Form — show errors on fields:
 * if ('error' in response) { setErrors(showApiError(response, 'Failed to save')); }
 *
 * @example
 * // Non-form — show all errors as toasts:
 * if ('error' in response) { showApiError(response, 'Failed to launch', true); }
 *
 * @example
 * // With .unwrap():
 * try { await mutation(data).unwrap(); }
 * catch (err) { showApiError(err, 'Failed', true); }
 */
export const showApiError = (
  error: unknown,
  fallback = 'An error occurred',
  showAllErrors = false,
): Record<string, string> => {
  const err = normalizeError(error);

  // a launch refused for want of Boost PRO: the paywall says it, not a toast
  if (handleProRequiredError(error)) {
    return {};
  }

  // 422 validation error
  if (err?.status === 422 || err?.data?.errors) {
    const fieldErrors = extractFieldErrors(error);
    const allMessages = err?.data?.errors
      ? Object.values(err.data.errors).flat().filter(Boolean)
      : [];
    const mainMessage = err?.data?.message;

    const hasFieldErrors = Object.keys(fieldErrors).length > 0;

    if (showAllErrors && allMessages.length > 0) {
      // Non-form: show every validation message as a separate toast
      allMessages.forEach((msg) => {
        void message.error(msg);
      });
    } else if (!hasFieldErrors) {
      // Form: show toast only when there are no field-level errors to display
      void message.error(mainMessage || allMessages[0] || fallback);
    }

    return fieldErrors;
  }

  // 403 forbidden
  if (err?.status === 403) {
    void message.error(err?.data?.message || 'You do not have permission to perform this action');
    return {};
  }

  // Other errors
  const msg = err?.data?.message || fallback;
  void message.error(msg);
  return {};
};

/** @deprecated Use `showApiError` — it now accepts both shapes automatically. */
export const showApiErrorFromResponse = showApiError;
